// Persistence + lifecycle helpers for the PWA install modal. Centralises the
// localStorage/sessionStorage keys and the suppression window (§2c, §2d).

import type { BeforeInstallPromptEvent } from "./pwaUtils";

const DISMISSED_KEY = "pwa_prompt_dismissed";
const DISMISSED_AT_KEY = "pwa_prompt_dismissed_at";
const INSTALLED_KEY = "pwa_installed";
// Session-scoped: stops the modal re-opening on every client-side route change
// within a single visit.
const AUTO_OPENED_SESSION_KEY = "pwa_modal_auto_opened";

const SUPPRESS_WINDOW_DAYS = 7;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

// True while the user is inside the cooldown started by tapping "Later".
export function isPromptSuppressed(): boolean {
  const dismissed = localStorage.getItem(DISMISSED_KEY);
  const dismissedAt = localStorage.getItem(DISMISSED_AT_KEY);
  if (!dismissed || !dismissedAt) return false;
  const daysSinceDismissed = (Date.now() - parseInt(dismissedAt, 10)) / MS_PER_DAY;
  return daysSinceDismissed < SUPPRESS_WINDOW_DAYS;
}

// Start the 7-day cooldown (tapping "Later").
export function suppressPrompt(): void {
  localStorage.setItem(DISMISSED_KEY, "true");
  localStorage.setItem(DISMISSED_AT_KEY, Date.now().toString());
}

export function markInstalled(): void {
  localStorage.setItem(INSTALLED_KEY, "true");
}

export function hasAutoOpenedThisSession(): boolean {
  return sessionStorage.getItem(AUTO_OPENED_SESSION_KEY) === "true";
}

export function markAutoOpened(): void {
  sessionStorage.setItem(AUTO_OPENED_SESSION_KEY, "true");
}

// Drive the native Android/Chrome install prompt and report the outcome.
export async function runInstallPrompt(
  deferredPrompt: BeforeInstallPromptEvent
): Promise<"accepted" | "dismissed" | "error"> {
  try {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    return outcome;
  } catch {
    return "error";
  }
}
